/**
 * The parts of a mission that do not move: ground, water, props, the band of country at the map's
 * edge, and the tells painted on the ground. Props that can be destroyed or switched (a breaker
 * thrown, a beacon set up) get their own mesh so it can be swapped; the rest are merged into one
 * draw call. Lights and the shadow frustum are `WorldLights`; the gun and the lit slot of a manned
 * emplacement are `Emplacements`, driven from `syncPropStates`.
 */

import { type Material, type Matrix4, Mesh, type Object3D, type Scene, type Texture } from 'three';
import { PROPS } from '../../sim/props';
import { Pcg32 } from '../../sim/rng';
import type { MissionDef, PropDef, World } from '../../sim/types';
import type { PixelCamera } from '../camera/PixelCamera';
import { floatsOnWater } from '../kit/boats';
import { MeshBuilder, place } from '../kit/MeshBuilder';
import type { SpanSite } from '../kit/spans';
import { createToonMaterial, OUTLINE } from '../materials/toon';
import type { Grade, Palette } from '../palette';
import { buildTerrain } from '../terrain/Terrain';
import { Emplacements } from './Emplacements';
import { mapEdgeBand } from './mapEdge';
import { PropGlow } from './PropGlow';
import { buildPropParts, initialSwitch, type PropSwitch, propScale } from './propModels';
import { wantedSwitch } from './propSwitches';
import { bridgeDecks, type Deck, dropSurface, openWaterAt, surfaceHeight } from './river';
import { buildRiverSurface } from './riverSurface';
import { groundMarks, wreckModel } from './staticParts';
import { terrainFromDef } from './terrainFromDef';
import { WorldLights } from './WorldLights';

/**
 * Destroyed props of these kinds leave nothing: a gap in the wire is the point. The expansion's walk-through
 * markers are listed too (the sim never destroys them, but the breaker panel and the strike beacon, which have
 * meshes of their own, can be taken away with `destroyProp`) - and the bridge, whose deck goes into the river.
 */
const VANISHES = new Set<PropDef['kind']>([
  'wire', 'sandbags', 'barrel', 'crate', 'alarmPost', 'proximityPost', 'searchlight',
  'breakerPanel', 'overlookMarker', 'observationPost', 'partisanCamp', 'culvert', 'strikeBeacon',
]);

/** A prop with a mesh of its own. `state` is null for a kind that cannot be switched. */
interface OwnProp {
  mesh: Mesh;
  def: PropDef;
  at: Matrix4;
  state: PropSwitch | null;
}

export class StaticWorld {
  /**
   * Height a person stands at: the terrain, the ice over a frozen river (not its bed), or the
   * deck of a bridge. Everything that places a moving thing on the ground should use this.
   */
  readonly groundY: (x: number, z: number) => number;
  private readonly lights: WorldLights;
  private readonly destructible = new Map<number, OwnProp>();
  /** Ids of the props that can be switched: the few `syncPropStates` has to look at each frame. */
  private readonly switched: number[] = [];
  private readonly glow: PropGlow;
  private readonly emplacements: Emplacements;
  /** Parts that turn (a searchlight's lamp), by prop id: their own meshes, so a view can rotate them. */
  private readonly heads = new Map<number, Mesh>();
  private readonly decks: Map<number, Deck>;
  private readonly bedY: (x: number, z: number) => number;
  private readonly propMaterial: Material;
  /** No outline: things painted on the ground (the pickup X, minefield furrows). */
  private readonly flatMaterial: Material;
  private readonly rng: Pcg32;
  private readonly meshes: Mesh[] = [];

  constructor(
    private readonly scene: Scene,
    private readonly def: MissionDef,
    private readonly palette: Palette,
    grade: Grade,
    private readonly gradient: Texture,
  ) {
    this.rng = new Pcg32(def.seed, 23);
    this.lights = new WorldLights(scene, grade);

    /* ---- ground, and the rivers Terrain.ts cannot draw (it has one rectangular water plane) */
    const terrainDef = terrainFromDef(def);
    const terrain = buildTerrain(terrainDef, palette, this.gradient);
    this.track(terrain.ground);
    if (terrain.water) this.track(terrain.water);
    for (const barrier of def.waterBarriers) this.track(buildRiverSurface(barrier, palette, this.gradient, def.props));
    this.decks = bridgeDecks(def.props, terrainDef.heightAt);
    this.bedY = terrainDef.heightAt;
    this.groundY = (x, z) => surfaceHeight(def, this.decks.values(), this.bedY(x, z), x, z);

    /* ---- props */
    this.propMaterial = createToonMaterial({ gradientMap: this.gradient, outline: OUTLINE.prop });
    this.flatMaterial = createToonMaterial({ gradientMap: this.gradient, outline: OUTLINE.none });
    this.glow = new PropGlow(scene, palette, this.gradient, def.timeOfDay);
    const merged = new MeshBuilder();
    for (const prop of def.props) {
      const state = initialSwitch(prop.kind);
      const { body, head } = buildPropParts(prop, palette, this.rng.fork(`prop${prop.id}`), state ?? 'on');
      const at = place(prop.x, this.seatY(prop), prop.z, prop.yaw, propScale(prop));
      // A bridge keeps a mesh of its own: the sim brings the deck down with its last pier (sim/hazards/spans.ts).
      if (PROPS[prop.kind].durability === 'none' && state === null && prop.kind !== 'bridge') {
        merged.merge(body, at);
        this.glow.lightStatic(prop, at, this.rng.fork(`glow${prop.id}`));
      } else {
        const mesh = this.addMesh(new MeshBuilder().merge(body, at), this.propMaterial);
        if (mesh) this.destructible.set(prop.id, { mesh, def: prop, at, state });
        if (state !== null) this.switched.push(prop.id);
        this.glow.light(prop, at, this.rng.fork(`glow${prop.id}`), state ?? 'on');
      }
      if (head) this.addHead(prop, head);
    }
    this.glow.finish();
    this.emplacements = new Emplacements(
      scene, palette, this.gradient, this.propMaterial, def.timeOfDay, def.props, (prop) => this.seatY(prop),
    );

    /* ---- what is painted on the ground: minefield tells and the X at the pickup (staticParts.ts) */
    const marks = groundMarks(def, palette, this.rng, this.groundY);
    merged.merge(marks.stakes, place());
    this.addMesh(merged, this.propMaterial);
    this.addMesh(marks.paint, this.flatMaterial, false);
    // The edge band rings the whole map: merged into one mesh, and kept out of the shadow pass.
    this.addMesh(mapEdgeBand(def, palette, this.rng.fork('edge'), this.groundY), this.propMaterial, false);
  }

  /**
   * Where a prop's y = 0 goes: into the ground. A bridge sits on its banks; a footing that belongs in
   * water stands on the BED, under any ice - but a HULL floats, so out of open water it rests on the
   * surface instead of being buried to its deck (kit/boats.ts).
   */
  private seatY(prop: PropDef, sink = 0.05): number {
    const deck = this.decks.get(prop.id);
    if (deck) return deck.baseY;
    const wet = PROPS[prop.kind].wet === true;
    const founded = wet && (!floatsOnWater(prop.kind) || openWaterAt(this.def, prop.x, prop.z));
    return (founded ? this.bedY(prop.x, prop.z) : this.groundY(prop.x, prop.z)) - sink;
  }

  /** Where a prop's y = 0 sits in the world, or null for an id that is not on the map (tests, debug). */
  seatOf(id: number): number | null {
    const prop = this.def.props.find((p) => p.id === id);
    return prop ? this.seatY(prop) : null;
  }

  /** Until a view turns it, the part faces the way the prop was placed - exactly the one-piece model. */
  private addHead(prop: PropDef, head: MeshBuilder): void {
    const mesh = this.addMesh(head, this.propMaterial);
    if (!mesh) return;
    mesh.position.set(prop.x, this.seatY(prop), prop.z);
    mesh.rotation.y = prop.yaw;
    mesh.scale.setScalar(propScale(prop));
    this.heads.set(prop.id, mesh);
  }

  /** The turning part of a prop (see `heads`), or null: none, or the prop has been destroyed. */
  turningPart(id: number): Object3D | null {
    return this.heads.get(id) ?? null;
  }

  /** True while prop `id` is showing a manned emplacement's tell (tests, debug overlay). */
  isManned(id: number): boolean {
    return this.emplacements.isManned(id);
  }

  private untrack(mesh: Mesh): void {
    this.scene.remove(mesh);
    mesh.geometry.dispose();
    const tracked = this.meshes.indexOf(mesh);
    if (tracked >= 0) this.meshes.splice(tracked, 1);
  }

  private track(mesh: Mesh): void {
    this.meshes.push(mesh);
    this.scene.add(mesh);
  }

  private addMesh(mb: MeshBuilder, material: Material, castShadow = true): Mesh | null {
    if (mb.triangleCount === 0) return null;
    const mesh = new Mesh(mb.build(), material);
    mesh.castShadow = castShadow;
    mesh.receiveShadow = true;
    this.track(mesh);
    return mesh;
  }

  /** Centre of a prop, for effects that only know its id. */
  propPosition(id: number): { x: number; z: number } | null {
    const prop = this.def.props.find((p) => p.id === id);
    return prop ? { x: prop.x, z: prop.z } : null;
  }

  /**
   * Switch a prop that has two states (propModels.ts `SWITCHED`): its mesh is swapped for the other
   * state's and its lamp lit or put out. A no-op for any other kind, a destroyed prop, or the state it is in.
   */
  setPropState(id: number, state: PropSwitch): void {
    const entry = this.destructible.get(id);
    if (!entry || entry.state === null || entry.state === state) return;
    const { body } = buildPropParts(entry.def, this.palette, this.rng.fork(`prop${id}`), state);
    const mesh = this.addMesh(new MeshBuilder().merge(body, entry.at), this.propMaterial);
    if (!mesh) return;
    this.untrack(entry.mesh);
    entry.mesh = mesh;
    entry.state = state;
    this.glow.light(entry.def, entry.at, this.rng.fork(`glow${id}`), state);
  }

  /**
   * Bring every switched prop into line with the sim (propSwitches.ts says how), turn the guns of the
   * manned emplacements to their gunners, and flash the strobes. Call once a frame: it does nothing
   * until something changes, and a resumed mission comes up right.
   */
  syncPropStates(world: World, time: number): void {
    for (const id of this.switched) {
      const entry = this.destructible.get(id);
      const want = entry ? wantedSwitch(this.def, world, entry.def) : null;
      if (want === 'gone') this.destroyProp(id, false);
      else if (want !== null) this.setPropState(id, want);
    }
    this.emplacements.update(world);
    this.glow.blink(time);
  }

  /**
   * Where a broken span lies: the water, ice or ground under the deck, in the bridge's own frame.
   * `seat` is the bridge's y = 0 (its higher bank), read before the deck is taken away.
   */
  private spanSite(prop: PropDef, seat: number): SpanSite {
    const drop = dropSurface(this.def, this.bedY(prop.x, prop.z), prop.x, prop.z);
    return { surfaceY: drop.y - seat, submerged: drop.submerged };
  }

  /** Swap a destroyed prop's model for wreckage over the same footprint (staticParts.ts). */
  destroyProp(id: number, leaveWreck = true): void {
    const entry = this.destructible.get(id);
    if (!entry) return;
    // Read the seat and the site BEFORE the deck goes: a bridge's y = 0 is the deck's own baseline.
    const seat = this.seatY(entry.def, 0);
    const site = this.spanSite(entry.def, seat);
    this.untrack(entry.mesh);
    this.destructible.delete(id);
    this.decks.delete(id); // a fallen deck carries nobody: walkers are back on the ford, or the ice, under it
    this.glow.douse(id);
    this.emplacements.remove(id);
    const head = this.heads.get(id);
    if (head) this.untrack(head);
    this.heads.delete(id);
    const { def } = entry;
    if (!leaveWreck || VANISHES.has(def.kind)) return;
    this.addMesh(wreckModel(def, this.palette, this.rng.fork(`wreck${id}`), seat, site), this.propMaterial);
  }

  /** Keep the shadow frustum on the view, snapped along the light's own axes (05 §8.2). */
  updateShadows(camera: PixelCamera): void {
    this.lights.follow(camera);
  }

  /** A stroke of lightning (02 §5): the sun and the sky lift for the instant it lasts. */
  setFlash(flash: number): void {
    this.lights.setFlash(flash);
  }

  /**
   * Take everything this class added out of the scene and free it: terrain, pond, river
   * surfaces, props, wrecks, ground tells, lights and the shadow map. Call when the mission ends.
   */
  dispose(): void {
    const materials = new Set<Material>([this.propMaterial, this.flatMaterial]);
    for (const mesh of this.meshes) {
      this.scene.remove(mesh);
      mesh.geometry.dispose();
      for (const m of Array.isArray(mesh.material) ? mesh.material : [mesh.material]) materials.add(m);
    }
    this.meshes.length = 0;
    this.destructible.clear();
    this.heads.clear();
    this.glow.dispose();
    this.emplacements.dispose();
    for (const m of materials) m.dispose();
    this.lights.dispose();
  }
}
